import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DefaultPageLayout } from "../ui/layouts/DefaultPageLayout";
import { Badge } from "../ui/components/Badge";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { Table } from "../ui/components/Table";
import { TextField } from "../ui/components/TextField";
import { Loader } from "../ui/components/Loader";
import { FeatherArrowLeft } from "@subframe/core";
import { FeatherChevronLeft } from "@subframe/core";
import { FeatherChevronRight } from "@subframe/core";
import { FeatherEye } from "@subframe/core";
import { FeatherFilePlus } from "@subframe/core";
import { FeatherSearch } from "@subframe/core";
import { journalsAPI, JournalTemplate } from "../services/api";

const RecurringJournalPageView: React.FC = () => {
  const navigate = useNavigate();

  // State management for templates
  const [templates, setTemplates] = useState<JournalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [itemsPerPage] = useState(10);

  // Fetch templates with pagination
  const fetchTemplatesData = useCallback(
    async (page: number = 1) => {
      try {
        setLoading(true);
        setError(null);

        const offset = (page - 1) * itemsPerPage;
        const response = await journalsAPI.fetchJournalTemplates(
          itemsPerPage,
          offset,
        );

        setTemplates(response.data.templates);

        // Update pagination state from response
        if (response.data.pagination) {
          setTotalItems(response.data.pagination.total);
          setHasMore(response.data.pagination.has_more);

          // Calculate total pages
          const pages = Math.ceil(
            response.data.pagination.total / itemsPerPage,
          );
          setTotalPages(pages);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
        setError("Failed to fetch recurring journal templates");
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage],
  );

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplatesData(1);
  }, [fetchTemplatesData]);

  // Filter templates based on search term
  const getFilteredTemplates = (): JournalTemplate[] => {
    if (!searchTerm.trim()) {
      return templates;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return templates.filter((template) => {
      const templateName = template.template_name.toLowerCase();
      const notes = template.notes?.toLowerCase() || "";
      const templateId = template.id.toString();

      return (
        templateName.includes(lowerSearchTerm) ||
        notes.includes(lowerSearchTerm) ||
        templateId.includes(lowerSearchTerm)
      );
    });
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (hasMore) {
      fetchTemplatesData(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchTemplatesData(currentPage - 1);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchTemplatesData(page);
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to format frequency
  const formatFrequency = (template: JournalTemplate): string => {
    switch (template.recurrence_frequency) {
      case "start_of_month":
        return "Monthly (Start)";
      case "end_of_month":
        return "Monthly (End)";
      case "daily":
        return "Daily";
      case "every_n_days":
        return `Every ${template.recurrence_interval} days`;
      default:
        return template.recurrence_frequency;
    }
  };

  // Helper function to format action code for display
  const formatActionCode = (actionCode: string): string => {
    const actionLabels: Record<string, string> = {
      cancel_recurring_before_end: "Cancel",
      update: "Update",
    };
    return actionLabels[actionCode] || actionCode;
  };

  // Helper function to get template status badge
  const getStatusBadge = (template: JournalTemplate) => {
    const isPaused = template.is_paused === "1" || template.is_paused === true;
    const isCancelled =
      template.is_cancelled === "1" || template.is_cancelled === true;

    // Check for pending review FIRST
    if (template.pending_review?.status === "pending") {
      return (
        <Badge variant="warning">
          Under Review - {formatActionCode(template.pending_review.action_code)}
        </Badge>
      );
    }

    if (isCancelled) {
      return <Badge variant="error">Ended</Badge>;
    } else if (isPaused) {
      return <Badge variant="warning">Paused</Badge>;
    } else {
      return <Badge variant="success">Active</Badge>;
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Header with back button */}
        <div className="flex w-full items-center gap-3 border-b border-solid border-neutral-border px-8 py-4">
          <IconButton
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/dashboard/journal")}
          />
          <span className="text-body font-body text-default-font">
            Back to Journals
          </span>
        </div>

        {/* Main content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 pb-8 pt-6 overflow-auto">
          {/* Page title and action button */}
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Recurring Journal Entries
            </span>
            <Button
              icon={<FeatherFilePlus />}
              onClick={() =>
                navigate("/journal/new", {
                  state: { defaultRecurringYes: true },
                })
              }
            >
              New Recurring Journal
            </Button>
          </div>

          {/* Search bar */}
          <div className="flex w-full items-start gap-8">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              variant="filled"
              label=""
              helpText=""
              icon={<FeatherSearch />}
            >
              <TextField.Input
                placeholder="Search entries..."
                value={searchTerm}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(event.target.value)
                }
              />
            </TextField>
          </div>

          {/* Table */}
          <div className="flex w-full flex-col items-start gap-8">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Template ID</Table.HeaderCell>
                  <Table.HeaderCell>Name / Description</Table.HeaderCell>
                  <Table.HeaderCell>Frequency</Table.HeaderCell>
                  <Table.HeaderCell>Start Date</Table.HeaderCell>
                  <Table.HeaderCell>Next Posting Date</Table.HeaderCell>
                  <Table.HeaderCell>Last Posted On</Table.HeaderCell>
                  <Table.HeaderCell>Total Entries</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={9}>
                    <div className="flex w-full items-center justify-center py-8">
                      <Loader />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : error ? (
                <Table.Row>
                  <Table.Cell colSpan={9}>
                    <div className="flex w-full items-center justify-center py-8">
                      <span className="text-body font-body text-error-600">
                        {error}
                      </span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : getFilteredTemplates().length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={9}>
                    <div className="flex w-full items-center justify-center py-8">
                      <span className="text-body font-body text-neutral-500">
                        {searchTerm.trim()
                          ? "No templates match your search"
                          : "No recurring journal templates found"}
                      </span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                getFilteredTemplates().map((template) => (
                  <Table.Row key={template.id}>
                    <Table.Cell className="align-top py-4">
                      <span
                        className="text-body-bold font-body-bold text-default-font underline cursor-pointer"
                        onClick={() =>
                          navigate(`/journal/recurring/${template.id}`)
                        }
                      >
                        RJ-{template.id}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="align-top py-4">
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-body font-body text-default-font">
                          {template.template_name}
                        </span>
                        {template.notes && (
                          <span className="text-caption font-caption text-subtext-color line-clamp-1">
                            {template.notes}
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="align-top py-4">
                      <span className="whitespace-nowrap text-body font-body text-neutral-500">
                        {formatFrequency(template)}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="align-top py-4">
                      <span className="whitespace-nowrap text-body font-body text-neutral-500">
                        {formatDate(template.starts_on)}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="align-top py-4">
                      <span className="whitespace-nowrap text-body font-body text-neutral-500">
                        {formatDate(template.next_posting_date || null)}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="align-top py-4">
                      <span className="whitespace-nowrap text-body font-body text-neutral-500">
                        {formatDate(template.last_processed_date || null)}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="align-top py-4">
                      <span className="whitespace-nowrap text-body font-body text-neutral-500">
                        {template.total_generated_count || 0} Posted
                      </span>
                    </Table.Cell>
                    <Table.Cell className="align-top py-4">
                      {getStatusBadge(template)}
                    </Table.Cell>
                    <Table.Cell className="align-top py-4">
                      <IconButton
                        icon={<FeatherEye />}
                        onClick={() => {
                          navigate(`/journal/recurring/${template.id}`);
                        }}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table>

            {/* Pagination Controls */}
            {totalPages > 1 && !loading && (
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-body font-body text-subtext-color">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                    {totalItems} results
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="neutral-tertiary"
                    size="small"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    icon={<FeatherChevronLeft />}
                  >
                    Previous
                  </Button>

                  {/* Page numbers */}
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum
                              ? "brand-primary"
                              : "neutral-tertiary"
                          }
                          size="small"
                          onClick={() => handlePageChange(pageNum)}
                          className="min-w-[32px]"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="neutral-tertiary"
                    size="small"
                    onClick={handleNextPage}
                    disabled={!hasMore}
                    icon={<FeatherChevronRight />}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default RecurringJournalPageView;
